import React from "react";
import { useTranslation } from "react-i18next";
import { useProjectStatusesRetrieval } from "@planzone/view-models";
import { Identifiable, ProjectStatus as P } from "@planzone/dto";

import { FolderIcon } from "@common/folderIcon";
import { Loader } from "../loader";
import { Section } from "../section";
import { ScrollPane } from "../scrollPane";
import { filterSearch } from "../utils";
import { Props } from "./types";
import { ProjectStatus } from "./projectStatus";
import styles from "./styles.scss";

function ProjectStatuses({ search, filter, setFilter, isOpen }: Props) {
  const { t } = useTranslation();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    statuses,
    retrieveProjectStatuses,
  } = useProjectStatusesRetrieval();
  const filteredStatuses = filterSearch(
    statuses.map((status) => ({
      ...status,
      label: t(`projectStatuses.${status.label}`, status.label),
    })),
    search,
  );
  const selectStatus = (status: Identifiable<P>) =>
    setFilter(filter ? [...filter, status] : [status]);
  const unSelectStatus = ({ uuid }: Identifiable<P>) =>
    filter && setFilter(filter.filter((status) => status.uuid !== uuid));

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectStatuses();
  }, []);

  return (
    <Section
      header={
        <div className={styles.header}>
          <FolderIcon className={styles.icon} />
          <p className={styles.title}>
            {t("common.filterMenu.projectStatuses.title")}
          </p>
        </div>
      }
      list={
        <ScrollPane>
          <div className={styles.list}>
            {!isRequestPending ? (
              [
                ...statuses.filter(
                  ({ uuid }) => !!filteredStatuses.find((s) => s.uuid === uuid),
                ),
              ]
                .sort((a, b) =>
                  t(`projectStatuses.${a.label}`, a.label).localeCompare(
                    t(`projectStatuses.${b.label}`, b.label),
                  ),
                )
                .map((status) => (
                  <ProjectStatus
                    key={status.uuid}
                    status={status}
                    isSelected={
                      !!filter &&
                      !!filter.find(({ uuid }) => uuid === status.uuid)
                    }
                    select={() => selectStatus(status)}
                    unSelect={() => unSelectStatus(status)}
                  />
                ))
            ) : (
              <Loader />
            )}
          </div>
        </ScrollPane>
      }
      isOpen={isOpen}
    />
  );
}

export { ProjectStatuses };
